import { usePage } from "@inertiajs/react";
import axios from "axios";
import { MouseEvent, useContext, useRef, useState } from "react";
import { BiUser } from "react-icons/bi";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { IoFlashOutline, IoHomeOutline } from "react-icons/io5";
// @ts-ignore
import { Grid } from "react-loader-spinner";

import FaqCardComponent from "@/components/Cards/FaqComponent";
import Layout from "@/components/Layout";
import FormModal from "@/components/Modal/FormModal";
import TextFieldComponent from "@/components/TextField";
import Typography from "@/components/Typography";
import theme from "@/constants/theme";
import { AlertContext } from "@/contexts/Alert";

import { ConsumerExtraInfos, ConsumerUnit, Container, Infos, UserInfos } from "./styles";

function Info(title: string, info: string) {
  return (
    <div className="info">
      <Typography variant="overline" color="grey500">
        {title}
      </Typography>
      <Typography variant="h5">{info}</Typography>
    </div>
  );
}

export default function PageData() {
  const { currentUser }: any = usePage().props;
  const userType = currentUser?.role;
  const [passwordInfos, setPasswordInfos] = useState({
    oldPassword: "",
    newPassword: "",
    newPasswordRepeated: "",
  });
  const [changePasswordWindow, setChangePasswordWindow] = useState(false);
  const [passwordFieldsTouched, setPasswordFieldsTouched] = useState({
    newPassword: false,
    newPasswordRepeated: false,
  });

  const passwordHasRequiredFormat = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/.test(
    passwordInfos.newPassword,
  );
  const passwordsMatch =
    passwordInfos.newPasswordRepeated.length > 0 &&
    passwordInfos.newPassword === passwordInfos.newPasswordRepeated;
  const canSubmitPasswordChange =
    passwordInfos.oldPassword.length > 0 && passwordHasRequiredFormat && passwordsMatch;

  const { showAlert } = useContext<any>(AlertContext);

  const carousel = useRef<HTMLDivElement>(null);

  function ChangePasswordRequisition(e: any) {
    e.preventDefault();

    if (!passwordHasRequiredFormat) {
      showAlert({
        message:
          "A senha precisa ter no mínimo 8 caracteres, uma letra minúscula, uma maiúscula, um número e um símbolo especial.",
        type: "error",
      });
      return;
    }

    if (!passwordsMatch) {
      showAlert({
        message: "As novas senhas precisam ser iguais.",
        type: "error",
      });
      return;
    }

    axios
      .patch("/api/users/password", passwordInfos)
      .then(() => {
        setPasswordInfos({
          oldPassword: "",
          newPassword: "",
          newPasswordRepeated: "",
        });
        setPasswordFieldsTouched({ newPassword: false, newPasswordRepeated: false });
        setChangePasswordWindow(false);
        showAlert({
          message: "Senha alterada com sucesso.",
          type: "success",
        });
      })
      .catch((error) => {
        showAlert({
          message: error.response?.data?.error || "Erro ao alterar sua senha.",
          type: "error",
        });
      });
  }

  const ClickLeft = (e: MouseEvent) => {
    e.preventDefault();
    if (carousel.current != null) {
      carousel.current.scrollLeft -= carousel.current.offsetWidth;
    }
  };

  const ClickRight = (e: MouseEvent) => {
    e.preventDefault();
    if (carousel.current != null) {
      carousel.current.scrollLeft += carousel.current.offsetWidth;
    }
  };

  return (
    <Layout>
      <Container>
        <Infos>
          <div className="presentation">
            <div className="title">
              <Typography variant="h3">Meus dados</Typography>
            </div>
            <div className="subtitle">
              <Typography variant="pLarge">
                Você encontra os seus dados na conta Geedee e pode realizar algumas ações, como
                substituir sua senha.
              </Typography>
            </div>
          </div>
          <UserInfos>
            {!currentUser ? (
              <div className="loading-center">
                <div className="loading-size">
                  <Grid color={theme.colors.grey500} />
                </div>
              </div>
            ) : (
              <>
                <hr />
                <div className="user-type">
                  <div className="circle-icon">
                    <BiUser />
                  </div>
                  <Typography variant="pLarge">
                    {userType == "consumer"
                      ? "Responsável"
                      : userType == "admin"
                        ? "Administrador"
                        : (userType == "manager" || userType == "investment_manager") && "Gestor"}
                  </Typography>
                </div>
                {Info("NOME", currentUser?.name)}
                {userType == "consumer" || userType == "admin"
                  ? Info("CPF ou CNPJ", currentUser?.cpf)
                  : ""}
                <div className="email-phone">
                  {Info("E-MAIL", currentUser?.email)}
                  {userType == "consumer" || userType == "admin"
                    ? Info("CELULAR", currentUser?.phone)
                    : ""}
                </div>
                {userType == "consumer" &&
                  currentUser?.invoice_due_date &&
                  Info("DIA DE VENCIMENTO", `Dia ${currentUser?.invoice_due_date}`)}
                {userType == "consumer" && (
                  <>
                    <hr />
                    <ConsumerExtraInfos>
                      <div className="user-type">
                        <div className="circle-icon">
                          <IoHomeOutline />
                        </div>
                        <Typography variant="pLarge">Endereço</Typography>
                      </div>
                      {Info("CEP", currentUser?.address.cep)}
                      {Info("ENDEREÇO", currentUser?.address.address)}
                      <div className="number-complement">
                        {Info("NÚMERO", currentUser?.address.number)}
                        {Info("COMPLEMENTO", currentUser?.address.complement)}
                      </div>
                      {Info("BAIRRO", currentUser?.address.neighborhood)}
                      <div className="city-state">
                        {Info("CIDADE", currentUser?.address.city)}
                        {Info("ESTADO", currentUser?.address.uf)}
                      </div>
                      <div className="card-one">
                        <FaqCardComponent
                          variant="dataSection"
                          buttonLabel="Mudar senha"
                          onClick={() => setChangePasswordWindow(!changePasswordWindow)}
                        />
                      </div>
                      <hr />
                      <div className="consumer-unit-header">
                        <div className="user-type">
                          <div className="circle-icon">
                            <IoFlashOutline />
                          </div>
                          <Typography variant="pLarge">
                            {currentUser.facilities.length > 1
                              ? "Unidades Consumidoras"
                              : "Unidade Consumidora"}
                          </Typography>
                        </div>
                        {currentUser.facilities.length > 1 && (
                          <div className="carousel-buttons">
                            <div className="button-left" onClick={(e) => ClickLeft(e)}>
                              <FiArrowLeft />
                            </div>
                            <div className="button-right" onClick={(e) => ClickRight(e)}>
                              <FiArrowRight />
                            </div>
                          </div>
                        )}
                      </div>
                      <ConsumerUnit ref={carousel}>
                        {currentUser.facilities.map((facility: any, index: number) => {
                          return (
                            <div className="facility-card-width" key={index}>
                              <div className="facility-card">
                                {Info("CEP", facility.address.cep)}
                                {Info("ENDEREÇO", facility.address.address)}
                                <div className="number-complement">
                                  {Info("NÚMERO", facility.address.number)}
                                  {Info("COMPLEMENTO", facility.address.complement)}
                                </div>
                                {Info("BAIRRO", facility.address.neighborhood)}
                                <div className="city-state">
                                  {Info("CIDADE", facility.address.city)}
                                  {Info("ESTADO", facility.address.uf)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </ConsumerUnit>
                      <div className="card-two">
                        <div className="card-two-center">
                          <FaqCardComponent variant="email" />
                        </div>
                      </div>
                    </ConsumerExtraInfos>
                  </>
                )}
                {userType != "consumer" && (
                  <div className="card-one">
                    <FaqCardComponent
                      variant="dataSection"
                      onClick={() => setChangePasswordWindow(!changePasswordWindow)}
                      buttonLabel="Mudar senha"
                    />
                  </div>
                )}
                {(userType == "manager" || userType == "investment_manager") && (
                  <div className="card-two">
                    <div className="card-two-center">
                      <FaqCardComponent variant="email" />
                    </div>
                  </div>
                )}
              </>
            )}
          </UserInfos>
        </Infos>
      </Container>
      <FormModal
        isOpen={changePasswordWindow}
        onClose={() => setChangePasswordWindow(false)}
        title="Mudar senha"
        description="Você pode alterar sua senha conforme desejar, basta preencher os campos abaixo."
        submitButtonTitle="Mudar senha"
        onSubmit={ChangePasswordRequisition}
        submitDisabled={!canSubmitPasswordChange}
        size="sm"
      >
        <div className="grid grid-cols-1 gap-4">
          <TextFieldComponent
            label="Informe a senha antiga"
            password
            value={passwordInfos.oldPassword}
            required
            onChange={(e) =>
              setPasswordInfos({
                ...passwordInfos,
                oldPassword: e.target.value,
              })
            }
          />
          <TextFieldComponent
            label="Informe a nova senha"
            password
            value={passwordInfos.newPassword}
            footnote={
              passwordFieldsTouched.newPassword && !passwordHasRequiredFormat
                ? "A senha precisa ter no mínimo 8 caracteres, uma letra minúscula, uma maiúscula, um número e um símbolo especial."
                : undefined
            }
            footnoteError={passwordFieldsTouched.newPassword && !passwordHasRequiredFormat}
            required
            onChange={(e) => {
              setPasswordFieldsTouched((fields) => ({ ...fields, newPassword: true }));
              setPasswordInfos({
                ...passwordInfos,
                newPassword: e.target.value,
              });
            }}
          />
          <TextFieldComponent
            label="Repita a nova senha"
            password
            value={passwordInfos.newPasswordRepeated}
            footnote={
              passwordFieldsTouched.newPasswordRepeated && !passwordsMatch
                ? "As novas senhas precisam ser iguais."
                : undefined
            }
            footnoteError={passwordFieldsTouched.newPasswordRepeated && !passwordsMatch}
            required
            onChange={(e) => {
              setPasswordFieldsTouched((fields) => ({ ...fields, newPasswordRepeated: true }));
              setPasswordInfos({
                ...passwordInfos,
                newPasswordRepeated: e.target.value,
              });
            }}
          />
        </div>
      </FormModal>
    </Layout>
  );
}
